import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gauge,
  Heart,
  Package,
  Plus,
  ShoppingCart,
  SquaresFour,
  Storefront,
  User,
} from '@phosphor-icons/react';

import { SearchPanel } from '../../design-system/components/desktop';
import { MY_ORDERS_TO } from '../../design-system/components/shells/navModel';
import { useAuth } from '../../hooks/useAuth';
import useDebounce from '../../hooks/useDebounce';
import { cardsAPI } from '../../api/modules/cards.module';
import { routes } from '../../routes/routes';
import { mediaUrl } from '../../utils/common';

/**
 * The command palette. Cmd-K anywhere, Ctrl-K away from a Mac.
 *
 * WHY IT IS NOT THE NAV BOX
 * -------------------------
 * The nav typeahead answers "which card". The palette answers "what do I want
 * to do" — list a card, open the cart, get to the dashboard — and only then
 * "which card", because once you are already reaching for a keyboard shortcut
 * you are navigating, not shopping. Two questions, two surfaces, one shortcut
 * pointing at the bigger of them.
 *
 * It is centred rather than anchored for the same reason: nothing on the page
 * summoned it, so there is nothing for it to hang off, and a panel that
 * appears under a control you did not touch reads as that control's menu.
 *
 * Built on the library's `SearchPanel` in its `palette` variant, so the
 * keyboard model here is the SAME code as the inline one — arrows move, Enter
 * opens, Escape closes, the pointer and the keyboard agree on the highlight,
 * matched substrings are marked. A second implementation of that is how the
 * two would quietly stop behaving alike.
 */

/** Everything you can do, and everywhere you can go, from an empty palette. */
function actionsFor({ isSeller }) {
  const actions = [
    { id: 'a-sell', title: 'List a card for sale', glyph: Plus, href: routes.CREATE_LISTING },
    /* `routes.CART`, not `routes.CHECKOUT` — same bug the header cart had.
       Only the cart page writes the handoff checkout reads, so jumping the
       palette straight to /checkout showed a R0.00 order. */
    { id: 'a-cart', title: 'Open the cart', glyph: ShoppingCart, href: routes.CART },
    /* NO "Track an order". It pointed at `/order-tracking`, which is a
       track-by-reference LOOKUP: it opens on an empty field asking for a
       tracking ID, and neither a buyer nor a seller has one to hand — a buyer
       wants THEIR orders and a seller wants the order they are looking at. It
       is an admin tool, reached from the admin console at
       `/admin-order-tracking`, and it does not belong in a palette that
       everyone shares. "Your orders" below goes where the buyer's orders
       actually are. */
  ];

  const places = [
    { id: 'p-market', title: 'Marketplace', glyph: Storefront, href: routes.MARKETPLACE },
    { id: 'p-sets', title: 'Games and sets', glyph: SquaresFour, href: routes.SETS },
    { id: 'p-collection', title: 'Collection', glyph: Heart, href: routes.COLLECTION },
    { id: 'p-orders', title: 'Your orders', glyph: Package, href: MY_ORDERS_TO },
    { id: 'p-dashboard', title: 'Dashboard', glyph: Gauge, href: routes.DASHBOARD },
    { id: 'p-profile', title: 'Profile and settings', glyph: User, href: routes.PROFILE },
  ];

  // A seller's console is a real destination; for a buyer it is a redirect to
  // a signup they did not ask for, so it is simply not offered.
  //
  // Inserted immediately after Dashboard rather than at a hard-coded index:
  // the index used to be 4, which was the end of a four-item list and is now
  // the middle of a six-item one, so the console landed between Games and
  // Collection. Finding the neighbour by id cannot rot the next time a place
  // is added.
  if (isSeller) {
    const after = places.findIndex((place) => place.id === 'p-dashboard');
    places.splice(after + 1, 0, {
      id: 'p-seller',
      title: 'Seller dashboard',
      glyph: Storefront,
      href: routes.SELLER_DASHBOARD,
    });
  }

  return [
    { id: 'actions', label: 'Actions', items: actions },
    { id: 'places', label: 'Go to', items: places },
  ];
}

/** Case-insensitive "every word appears somewhere", same rule as the panel. */
function matching(groups, query) {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return groups;

  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        tokens.every((token) => item.title.toLowerCase().includes(token)),
      ),
    }))
    .filter((group) => group.items.length > 0);
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { isSeller } = useAuth();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cards, setCards] = useState([]);

  const debounced = useDebounce(query.trim(), 250);
  const seqRef = useRef(0);

  // Cards are only fetched while the palette is actually open. It is mounted
  // on every page, and a component that quietly polls an endpoint for a panel
  // nobody has summoned is a background cost with no user.
  useEffect(() => {
    if (!open || debounced.length < 2) {
      setCards([]);
      return undefined;
    }

    const seq = ++seqRef.current;
    let live = true;

    cardsAPI
      .getLookUpCards({ search: debounced, page: 1, page_size: 5 })
      .then((response) => {
        if (!live || seq !== seqRef.current) return;
        setCards((response?.data || []).slice(0, 5));
      })
      .catch(() => {
        if (!live || seq !== seqRef.current) return;
        setCards([]);
      });

    return () => {
      live = false;
    };
  }, [open, debounced]);

  const groups = useMemo(() => {
    const base = matching(actionsFor({ isSeller }), query);

    if (cards.length === 0) return base;

    return [
      ...base,
      {
        id: 'cards',
        label: 'Cards',
        count: cards.length,
        items: cards.map((card) => ({
          id: card.id,
          kicker: card.catalog_group,
          title: card.product_name,
          meta: card.card_number,
          thumb: card.image_url ? mediaUrl(card.image_url) : undefined,
          href: `${routes.MARKETPLACE}/card/${card.id}`,
        })),
      },
    ];
  }, [isSeller, query, cards]);

  return (
    <SearchPanel
      variant="palette"
      panelClassName="dsa-palette"
      query={query}
      onQueryChange={setQuery}
      onOpenChange={(next) => {
        setOpen(next);
        // A palette that reopens holding the last thing you typed is a palette
        // showing yesterday's answer. It opens empty, every time.
        if (!next) setQuery('');
      }}
      groups={groups}
      placeholder="Search, or jump to…"
      fieldLabel="Search and commands"
      onSelect={(item) => {
        if (item?.href) navigate(item.href);
      }}
      onSubmit={(value) => {
        const trimmed = String(value || '').trim();
        if (trimmed) navigate(`${routes.SEARCH}?q=${encodeURIComponent(trimmed)}`);
      }}
      emptyTitle="Nothing matches that"
      emptyText="Try a card name, or the name of a page."
      enterHint={
        <>
          <span className="dsk-kbd">Enter</span>
          {query ? ' to search the marketplace' : ' to open the highlighted row'}
        </>
      }
    />
  );
}

export default CommandPalette;
